/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import debounce from 'lodash.debounce'
import { useCreateOrUpdateDocumentRowValueWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useGetRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import { DocumentRowValue } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { showWarningSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useDocumentCurrentStateViewContext } from '@/components/image-zoom-pan/providers/DocumentCurrentStateViewProvider'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import { useDocumentRowsContext } from './providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from './providers/DocumentRowValuesProvider'
import {
  buildFieldIdentifier,
  FieldIdentifier,
  useDocumentsChangeSetsContext,
} from './providers/DocumentsChangeSetsProvider'
import { useFieldErrorContext } from './providers/FieldErrorProvider'
import { getRowValuesMainValues } from './useGetValueData'

export type DataLineageValues = {
  original_value: string | null
  rule_value: string | null
  manual_value: string | null
  final_value: string | null
}

type UseFieldValueProps = {
  field?: ProjectGridField
  documentRowId?: string
}

type UseFieldValueReturn = {
  value: string
  dataLineageValues: DataLineageValues
  hasManualValue: boolean
  refId: string | null
  jsonValue: Record<string, any> | null
  onManualChange: (
    newValues: {
      manualValue: string | null
      refId?: string | null
      jsonObject?: Record<string, any> | null
    },
    setFinalValue?: boolean,
  ) => void
  onCategoryChange: (newCategoryId: string | null) => void
  clearValue: () => void
  documentRowValue?: DocumentRowValue
  wasAutoFilled: boolean
  metadata: Record<string, any>
}

const NULL_INTERNAL_VALUE = {
  value: null,
  refId: null,
  jsonValue: null,
}

export default function useFieldValue({
  field,
  documentRowId,
}: UseFieldValueProps): UseFieldValueReturn {
  const { document } = useDocumentContext()
  const { baseRow } = useDocumentRowsContext()
  const fieldId = field?.id || ''
  // documentRow from context is second because there might be one for the base grid,
  // so we don't want to override the value passed for fields in table
  const rowId = documentRowId || baseRow?.id || null
  const { setFieldError } = useFieldErrorContext()
  const { queryKey, getFieldDocumentRowValue } = useDocumentRowValuesContext()
  const { documentCurrentStateView } = useDocumentCurrentStateViewContext()
  const documentsChangeSetsContextData = useDocumentsChangeSetsContext()
  const {
    markFieldsUpdating,
    isFieldUpdating,
    clearFieldsBeingUpdated,
    consumeRecentSystemChange,
  } = documentsChangeSetsContextData

  const documentRowValue = getFieldDocumentRowValue(fieldId, rowId)
  const {
    manual_value = null,
    rule_value = null,
    original_value = null,
    final_value = null,
    final_ref_id,
    final_value_json = null,
    project_content_category_item_id,
  } = documentRowValue ? documentRowValue : ({} as DocumentRowValue)

  const [internal, setInternal] = useState<{
    value: string | null
    refId: string | null
    jsonValue: Record<string, any> | null
  }>(NULL_INTERNAL_VALUE)

  const fieldIsBeingUpdated = useMemo(
    () => isFieldUpdating(fieldId, rowId),
    [fieldId, rowId, isFieldUpdating],
  )

  const wasFieldUpdatingRef = useRef(false)

  useEffect(() => {
    if (fieldIsBeingUpdated) {
      wasFieldUpdatingRef.current = true
      return
    }

    if (wasFieldUpdatingRef.current) {
      wasFieldUpdatingRef.current = false
      setInternal(NULL_INTERNAL_VALUE)
    }
  }, [fieldIsBeingUpdated])

  const { selectedWorkflowState } = useSelectedWorkflowStateContext()
  const getRelevantWorkflowState = useGetRelevantWorkflowState()
  const state = selectedWorkflowState || getRelevantWorkflowState(document)

  const { createOrUpdateDocumentRowValue } =
    useCreateOrUpdateDocumentRowValueWithDocChange({
      documentId: document?.id,
      workflowId: state?.workflow_id || '',
      workflowStateId: state?.id || '',
      listQueryKey: queryKey,
      documentsChangeSetsContextData,
      onError: () => {
        clearFieldsBeingUpdated([buildFieldIdentifier(fieldId, rowId)])
        setInternal(NULL_INTERNAL_VALUE)
        setFieldError(fieldId, rowId, 'Unable to update field.')
      },
    })

  const createOrUpdateDocumentRowValueRef = useRef(
    createOrUpdateDocumentRowValue,
  )

  const updateValue = useCallback(
    function (
      newValues: {
        manualValue?: string | null
        categoryItemId?: string | null
        refId?: string | null
        jsonObject?: Record<string, any> | null
      },
      setFinalValue?: boolean,
    ) {
      const updatedValues: Partial<DocumentRowValue> = {
        manual_value: newValues.manualValue ?? null,
        project_content_category_item_id: newValues.categoryItemId || null,
        manual_ref_id: newValues.refId || null,
        manual_value_json: newValues.jsonObject || null,
      }
      if (setFinalValue) {
        updatedValues.final_value = updatedValues.manual_value
        updatedValues.final_ref_id = updatedValues.manual_ref_id
        updatedValues.final_value_json = updatedValues.manual_value_json
      }

      const valueId = documentRowValue?.id || generateUuid()
      const updatedDocumentRowValue = {
        ...documentRowValue,
        id: valueId,
        project_grid_field_id: fieldId,
        document_row_id: rowId,
        ...updatedValues,
      } as DocumentRowValue
      if (!documentRowValue) {
        return createOrUpdateDocumentRowValueRef.current(
          updatedDocumentRowValue,
          'new',
        )
      }

      return createOrUpdateDocumentRowValueRef.current(
        updatedDocumentRowValue,
        'update',
      )
    },
    [documentRowValue, fieldId, rowId],
  )

  // If we switch documents, update the ref
  useEffect(() => {
    createOrUpdateDocumentRowValueRef.current = createOrUpdateDocumentRowValue
  }, [document?.id, createOrUpdateDocumentRowValue])

  const debouncedHandleChange = useMemo(
    () =>
      debounce(
        (
          newValues: {
            manualValue?: string | null
            refId?: string | null
            jsonObject?: Record<string, any> | null
          },
          setFinalValue?: boolean,
        ) => {
          setFieldError(fieldId, rowId, '')
          updateValue(newValues, setFinalValue)
        },
        500,
      ),
    [fieldId, rowId, setFieldError, updateValue],
  )

  const onCategoryChange = useCallback(
    (newCategoryId: string | null) => {
      setInternal(NULL_INTERNAL_VALUE)
      setFieldError(fieldId, rowId, '')
      updateValue({
        categoryItemId: newCategoryId,
      })
    },
    [rowId, fieldId, setFieldError, updateValue],
  )

  const setManualChangeAsFieldUpdating = useCallback(() => {
    if (!fieldId || !rowId) return

    const fieldIdentifier: FieldIdentifier = buildFieldIdentifier(
      fieldId,
      rowId,
    )
    markFieldsUpdating(
      [fieldIdentifier],
      field?.project_grid_field_type.code === 'live-list-picker',
    )

    // The rules engine may have replaced this field's value moments ago, in
    // which case the user is editing on top of a value that changed under them.
    if (field?.name && consumeRecentSystemChange(fieldId, rowId)) {
      showWarningSnackbar(
        `${field.name} was updated by the system moments before your edit. Please review to confirm the value appears as you expect.`,
        {
          autoHideDuration: 5000,
        },
      )
    }
  }, [
    consumeRecentSystemChange,
    field?.name,
    field?.project_grid_field_type.code,
    fieldId,
    markFieldsUpdating,
    rowId,
  ])

  const onManualChange = useCallback(
    (
      newValues: {
        manualValue: string | null
        // If refId or jsonObject are omitted, we will set them to null. There should never be a
        // when we change the manual value, but not the refId or jsonObject. If the refId or jsonObject are involved,
        // we should pass them in.
        refId?: string | null
        jsonObject?: Record<string, any> | null
      },
      setFinalValue?: boolean,
    ) => {
      const isNoneOption =
        newValues.manualValue === 'None' &&
        (newValues.refId === 'None' ||
          newValues.jsonObject?.entity_id === 'None')
      setInternal({
        value: isNoneOption ? '' : newValues.manualValue,
        refId:
          newValues.refId !== undefined
            ? isNoneOption
              ? null
              : newValues.refId
            : null,
        jsonValue:
          newValues.jsonObject !== undefined
            ? isNoneOption
              ? null
              : newValues.jsonObject
            : null,
      })

      if (isNoneOption) {
        setFieldError(fieldId, rowId, '')
        updateValue({ manualValue: '' }, setFinalValue)
        // If we are setting the final value, it means we want an immediate update, so we don't want to debounce it
      } else if (newValues.manualValue !== null && !setFinalValue) {
        debouncedHandleChange(newValues)
      } else {
        setFieldError(fieldId, rowId, '')
        updateValue(newValues, setFinalValue)
      }
      setManualChangeAsFieldUpdating()
    },
    [
      setManualChangeAsFieldUpdating,
      setFieldError,
      fieldId,
      rowId,
      updateValue,
      debouncedHandleChange,
    ],
  )

  const clearValue = useCallback(() => {
    setInternal(NULL_INTERNAL_VALUE)
    setFieldError(fieldId, rowId, '')
    if (manual_value !== null) {
      updateValue({})
      setManualChangeAsFieldUpdating()
    }
  }, [
    setFieldError,
    fieldId,
    rowId,
    manual_value,
    updateValue,
    setManualChangeAsFieldUpdating,
  ])

  const rowValueMainData = getRowValuesMainValues(documentRowValue)

  return {
    value:
      documentCurrentStateView === 'validation'
        ? internal.value ??
          rowValueMainData.value ??
          project_content_category_item_id ??
          ''
        : final_value ?? '',
    hasManualValue: manual_value !== null,
    jsonValue:
      documentCurrentStateView === 'validation'
        ? internal.jsonValue ?? rowValueMainData.jsonValue
        : final_value_json,
    refId:
      documentCurrentStateView === 'validation'
        ? internal.refId ?? rowValueMainData.refId
        : final_ref_id,
    dataLineageValues: {
      original_value,
      rule_value,
      manual_value,
      final_value,
    },
    onManualChange,
    onCategoryChange,
    clearValue,
    documentRowValue,
    wasAutoFilled: !!rule_value && !original_value && !manual_value,
    metadata: documentRowValue?.metadata || {},
  }
}
